import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Input,
  Button,
  Checkbox,
  useToast,
} from "@swordsweeper/ssi-ui-component-library";
import { useFormHandlers } from "@swordsweeper/react-forms";
import * as Yup from "yup";
import AuthPageLayout from "@src/components/widgets/AuthPageLayout";
import * as SignUpApi from "@src/api/SignUpApi";
import styles from "./SignUpPage.module.scss";

const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const schema = Yup.object().shape({
  firstName: Yup.string().trim().required("First name is required."),
  lastName: Yup.string().trim().required("Last name is required."),
  email: Yup.string()
    .trim()
    .required("Email address is required.")
    .test(
      "email-format",
      "Must be a valid email address.",
      (v) => !v || emailRegex.test(v),
    ),
  password: Yup.string()
    .required("Password is required.")
    .min(8, "Password must be at least 8 characters."),
  passwordConfirm: Yup.string()
    .required("Please re-enter your password.")
    .oneOf([Yup.ref("password")], "Passwords do not match."),
  agreedToTerms: Yup.boolean().oneOf(
    [true],
    "You must agree to the End User License Agreement and Privacy Policy.",
  ),
});

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  passwordConfirm: string;
  agreedToTerms: boolean;
}

type FieldName = keyof FormData;

const INITIAL_DATA: FormData = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  passwordConfirm: "",
  agreedToTerms: false,
};

export default function SignUpPage(): React.JSX.Element {
  const navigate = useNavigate();
  const showToast = useToast();
  const { formData, handleUpdate, isValid } = useFormHandlers<FormData>(
    INITIAL_DATA,
    schema,
  );
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  // Server-side error for "email already exists" — distinct from client validation
  const [emailTakenError, setEmailTakenError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateField = async (name: FieldName) => {
    try {
      await schema.validateAt(name, formData);
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setErrors((prev) => ({ ...prev, [name]: err.message }));
      }
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await SignUpApi.clinicSignUp(formData);
      navigate("/sign-up/email-sent", {
        state: { email: formData.email },
      });
    } catch (error: any) {
      const body = error?.response?.body;
      const fieldErrors = body && typeof body === "object" ? body : null;

      if (fieldErrors?.email) {
        const msg = Array.isArray(fieldErrors.email)
          ? fieldErrors.email[0]
          : String(fieldErrors.email);
        setEmailTakenError(msg);
        return;
      }

      if (fieldErrors) {
        const next: Partial<Record<FieldName, string>> = {};
        for (const key of Object.keys(fieldErrors) as FieldName[]) {
          const value = (fieldErrors as Record<string, unknown>)[key];
          next[key] = Array.isArray(value) ? String(value[0]) : String(value);
        }
        if (Object.keys(next).length > 0) {
          setErrors(next);
          return;
        }
      }

      const message =
        body?.detail ||
        error?.message ||
        "Something went wrong creating your account. Please try again.";
      showToast({ message, variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const emailErrorMessage = emailTakenError ?? errors.email;
  const passwordConfirmErrorMessage = errors.passwordConfirm;

  return (
    <AuthPageLayout>
      <div className={styles.formCard}>
        <h1 className={styles.title}>Account Setup</h1>

        <div className={styles.fieldStack}>
          <div className={styles.row}>
            <div className={styles.field}>
              <Input
                label="First Name"
                defaultValue=""
                value={formData.firstName}
                onChange={(value: string) =>
                  handleUpdate({ name: "firstName", value })
                }
                onBlur={() => validateField("firstName")}
                error={!!errors.firstName}
                width="100%"
                required
                data-testid="sign-up-first-name"
              />
              {errors.firstName && (
                <p className={styles.errorMessage}>
                  <i className="fa-light fa-circle-exclamation" aria-hidden />
                  <span>{errors.firstName}</span>
                </p>
              )}
            </div>

            <div className={styles.field}>
              <Input
                label="Last Name"
                defaultValue=""
                value={formData.lastName}
                onChange={(value: string) =>
                  handleUpdate({ name: "lastName", value })
                }
                onBlur={() => validateField("lastName")}
                error={!!errors.lastName}
                width="100%"
                required
                data-testid="sign-up-last-name"
              />
              {errors.lastName && (
                <p className={styles.errorMessage}>
                  <i className="fa-light fa-circle-exclamation" aria-hidden />
                  <span>{errors.lastName}</span>
                </p>
              )}
            </div>
          </div>

          <div className={styles.field}>
            <Input
              label="Email Address"
              defaultValue=""
              type="email"
              value={formData.email}
              onChange={(value: string) => {
                handleUpdate({ name: "email", value });
                setEmailTakenError(null);
              }}
              onBlur={() => validateField("email")}
              error={!!emailErrorMessage}
              width="100%"
              required
              data-testid="sign-up-email"
            />
            {emailErrorMessage && (
              <p className={styles.errorMessage}>
                <i className="fa-light fa-circle-exclamation" aria-hidden />
                <span>{emailErrorMessage}</span>
              </p>
            )}
          </div>

          <div className={styles.field}>
            <Input
              label="Enter Password"
              defaultValue=""
              type="password"
              value={formData.password}
              onChange={(value: string) =>
                handleUpdate({ name: "password", value })
              }
              onBlur={() => validateField("password")}
              error={!!errors.password}
              width="100%"
              required
              autoComplete={false}
              data-testid="sign-up-password"
            />
            {errors.password && (
              <p className={styles.errorMessage}>
                <i className="fa-light fa-circle-exclamation" aria-hidden />
                <span>{errors.password}</span>
              </p>
            )}
          </div>

          <div className={styles.field}>
            <Input
              label="Re-enter Password"
              defaultValue=""
              type="password"
              value={formData.passwordConfirm}
              onChange={(value: string) =>
                handleUpdate({ name: "passwordConfirm", value })
              }
              onBlur={() => validateField("passwordConfirm")}
              error={!!passwordConfirmErrorMessage}
              width="100%"
              required
              autoComplete={false}
              data-testid="sign-up-password-confirm"
            />
            {passwordConfirmErrorMessage && (
              <p className={styles.errorMessage}>
                <i className="fa-light fa-circle-exclamation" aria-hidden />
                <span>{passwordConfirmErrorMessage}</span>
              </p>
            )}
          </div>
        </div>

        <div className={styles.termsRow}>
          <Checkbox
            name="agreedToTerms"
            value="agreedToTerms"
            checked={formData.agreedToTerms}
            onChange={(value: boolean) =>
              handleUpdate({ name: "agreedToTerms", value })
            }
            label={
              <span className={styles.termsLabel}>
                By checking this, you agree to{" "}
                <a
                  href="/terms-and-conditions/"
                  className={styles.termsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  OrderEasy&rsquo;s End User License Agreement
                </a>{" "}
                and{" "}
                <a
                  href="/privacy-policy/"
                  className={styles.termsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
                .
              </span>
            }
            data-testid="sign-up-terms-checkbox"
          />
        </div>
        {errors.agreedToTerms && (
          <p className={styles.errorMessage}>
            <i className="fa-light fa-circle-exclamation" aria-hidden />
            <span>{errors.agreedToTerms}</span>
          </p>
        )}

        <Button
          className={`btn btn-primary btn-md ${styles.submitButton}`}
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
          data-testid="sign-up-submit"
        >
          {isSubmitting ? "Creating Account..." : "Create Account"}
        </Button>
      </div>
    </AuthPageLayout>
  );
}
